import { uuid } from '../../constants/constants';
import { setColorName, setColorStyle } from '../../utils/paletteColors';

/**
 * Palette d'une `configuration` (`colors`, `[{ label, style, name? }]`) : les
 * gestes de l'onglet Couleurs, une seule fois (cf.
 * docs/architecture-logique-ui.md). Chaque opération renvoie un patch.
 *
 * Le `label` est l'identifiant d'une couleur (les styles s'y réfèrent) : il ne
 * change jamais, le nom saisi vit dans `name` (cf. utils/paletteColors).
 */

/** Une couleur CSS au format d'un réglage du classique (`{ label, style }`). */
export const paletteColor = (value) => (value ? { label: value, style: value } : undefined);

/** Couleurs livrées avec toute palette : elles ne se suppriment pas. */
export const PROTECTED_COLOR_LABELS = ['white', 'black', 'transparent'];

export const isProtectedColor = (label) => PROTECTED_COLOR_LABELS.includes(label);

const colorsOf = (configuration) => configuration?.colors || [];

const editedCopy = (configuration, edit) => {
  const colors = colorsOf(configuration).map((color) => ({ ...color }));
  edit(colors);
  return { colors };
};

/**
 * Une couleur déjà dans la palette ne s'ajoute pas deux fois : `null`.
 *
 * `source` : couleur adoptée telle quelle (celle d'une section liée) quand son
 * CSS est celui ajouté. Elle garde son label, auquel les boxes de la section se
 * réfèrent, et son nom, sauf si ce label est déjà pris dans la palette.
 */
export function addPaletteColor(configuration, style, source = null) {
  const colors = colorsOf(configuration);
  if (!style || colors.some((color) => color.style === style)) return null;
  const adopted = source?.style === style && source.label
    && !colors.some((color) => color.label === source.label);
  const color = adopted
    ? { label: source.label, style, ...(source.name ? { name: source.name } : {}) }
    : { label: `color${uuid()}`, style };
  return { colors: [color, ...colors] };
}

export function updatePaletteColor(configuration, label, style) {
  return editedCopy(configuration, (colors) => setColorStyle(colors, label, style));
}

export function renamePaletteColor(configuration, label, name) {
  return editedCopy(configuration, (colors) => setColorName(colors, label, name));
}

export function removePaletteColor(configuration, label) {
  if (isProtectedColor(label)) return null;
  return { colors: colorsOf(configuration).filter((color) => color.label !== label) };
}

/** La couleur de la palette qui porte ce CSS : ce que stockent les styles. */
export function paletteColorOfStyle(configuration, style) {
  return colorsOf(configuration).find((color) => color.style === style) || null;
}

/**
 * Une couleur choisie (CSS) au format des réglages : celle de la palette qui
 * la porte, sinon une couleur libre (`label` = son CSS, comme les thèmes
 * d'avant).
 */
export function asPaletteColor(configuration, style) {
  if (!style) return null;
  if (style === 'transparent') return { label: 'transparent', style: 'transparent' };
  return paletteColorOfStyle(configuration, style) || { label: style, style };
}
